import type { InternalAxiosRequestConfig } from 'axios'
import axios, { type AxiosError } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { refreshSession, SessionRefreshError } from '@/stores/refreshSession'
import type { SyncFilter, SyncFilterUpdate } from '@/types/syncFilter'

/** Axios config plus the retry counter the 401 handler sets, so a retry never loops. */
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  authRetries?: number
}

/**
 * Two, not one: the first retry may only have adopted another tab's access
 * token, and that tab may have gone to sleep before its own hour ran out. The
 * second attempt is the one that rotates.
 */
const MAX_AUTH_RETRIES = 2


const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Unauthenticated client for public, token-based endpoints (customer view,
// management-email approvals) that are not tied to a logged-in session.
export const publicClient = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use((config) => {
  const { token, activeTenantId } = useAuthStore.getState()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  } else if (import.meta.env.DEV) {
    console.warn('[API] No auth token available for request:', config.url)
  }
  if (activeTenantId) {
    config.headers['X-Tenant-ID'] = activeTenantId
  }
  return config
})

/** Drop the session and send the user to the login screen. */
function endSession(): void {
  useAuthStore.getState().logout()
  window.location.href = '/login'
}

apiClient.interceptors.response.use(
  (response) => {
    // An HTML body on an API call means an infrastructure layer (CDN error
    // page, SPA fallback) swallowed the real response — reject instead of
    // letting pages render index.html as data.
    const contentType = String(response.headers['content-type'] ?? '')
    if (contentType.includes('text/html')) {
      return Promise.reject(
        new Error(`API returned HTML instead of JSON for ${response.config.url}`),
      )
    }
    const expiresAt = response.headers['x-session-expires-at']
    if (expiresAt) {
      _sessionExpiresAt = expiresAt
    }
    return response
  },
  async (error: AxiosError) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error)
    }
    const originalRequest = error.config as RetryableRequestConfig
    const attempts = originalRequest.authRetries ?? 0
    if (attempts >= MAX_AUTH_RETRIES) {
      endSession()
      return Promise.reject(error)
    }
    originalRequest.authRetries = attempts + 1

    try {
      // Single-flight per browser: parallel 401s here and 401s in other tabs
      // all settle on one rotation, so nobody presents a blacklisted token.
      const token = await refreshSession()
      originalRequest.headers.Authorization = `Bearer ${token}`
      return apiClient(originalRequest)
    } catch (refreshError) {
      console.error('[auth] Session refresh failed:', refreshError)
      // Only a token the server itself rejected ends the session. A network
      // failure must leave the stored pair alone, or a laptop waking before
      // its wifi signs the user out for good.
      if (refreshError instanceof SessionRefreshError && refreshError.sessionExpired) {
        endSession()
      }
      return Promise.reject(error)
    }
  }
)

// HumanTasks API
export const humanTasksAPI = {
  get: (id: string) => apiClient.get(`/human-tasks/${id}/`),
  addNote: (id: string, note: string) =>
    apiClient.post(`/human-tasks/${id}/notes/`, { note }),
  setOutcome: (id: string, outcome: string, data?: Record<string, unknown>) =>
    apiClient.post(`/human-tasks/${id}/outcome/`, { outcome, ...data }),
  collectionAgents: () => apiClient.get('/human-tasks/collection-agents/'),
  /** All active tenant users — for flow Task-node assignment (any user, not just agents). */
  assignableUsers: () => apiClient.get('/human-tasks/assignable-users/'),
  /** CSV export of the task worklist; honors the same filters/ordering as the list endpoint. */
  exportHumanTasks: (params?: Record<string, unknown>) =>
    apiClient.get('/human-tasks/export/', { params, responseType: 'blob' }),
  assign: (humanTaskId: string, agentId: string) =>
    apiClient.post(`/human-tasks/${humanTaskId}/assign/`, { agent_id: agentId }),
  unassign: (humanTaskId: string) =>
    apiClient.post(`/human-tasks/${humanTaskId}/unassign/`),
  transition: (humanTaskId: string, newStatus: string) =>
    apiClient.post(`/human-tasks/${humanTaskId}/transition/`, { status: newStatus }),
}

// Promises API
export const promisesAPI = {
  list: (params?: Record<string, unknown>) =>
    apiClient.get('/promises/', { params }).then(r => r.data),
  markFulfilled: (id: string) =>
    apiClient.post(`/promises/${id}/mark-fulfilled/`).then(r => r.data),
  markBroken: (id: string) =>
    apiClient.post(`/promises/${id}/mark-broken/`).then(r => r.data),
}

// Telephony API
export const telephonyAPI = {
  dial: (humanTaskId: string) => apiClient.post('/telephony/dial/', { human_task_id: humanTaskId }),
  hangup: (callId: string) => apiClient.post(`/telephony/hangup/`, { call_id: callId }),
}

// Settings API
export interface SyncConfig {
  id: string
  sync_frequency: 'manual' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom'
  sync_interval_hours: number
  payment_refresh_interval_hours: number
  last_payment_refresh_at: string | null
  is_enabled: boolean
  last_sync_at: string | null
  last_sync_status: string
  last_sync_record_count: number
  last_sync_error: string
  next_sync_at: string | null
  is_due_for_sync: boolean
  updated_at: string
}

export const settingsAPI = {
  // Tenant settings
  getTenantSettings: () => apiClient.get('/settings/tenant/').then(r => r.data),
  updateTenantSettings: (data: Record<string, unknown>) =>
    apiClient.patch('/settings/tenant/', data).then(r => r.data),
  getLLMFeatures: () => apiClient.get('/settings/llm-features/').then(r => r.data),
  updateLLMFeature: (feature: string, data: Record<string, unknown>) =>
    apiClient.patch(`/settings/llm-features/${feature}/`, data).then(r => r.data),
  testEmail: (recipient: string) =>
    apiClient.post('/collections/email-send/', { mode: 'test', recipient }),

  // Sync settings
  getSyncConfig: () => apiClient.get('/settings/sync/').then(r => r.data as SyncConfig),
  updateSyncConfig: (data: Record<string, unknown>) =>
    apiClient.patch('/settings/sync/', data).then(r => r.data),
  triggerSync: (opts?: { force_full?: boolean }) =>
    apiClient.post('/settings/sync/trigger/', opts).then(r => r.data),

  // Tenant profile
  getTenantProfile: () => apiClient.get('/settings/tenant-profile/').then(r => r.data),
  updateTenantProfile: (data: { name: string }) =>
    apiClient.patch('/settings/tenant-profile/', data).then(r => r.data),

  // Inbound email domain — registration is automatic on Mailgun Domain save;
  // there is no provision endpoint.
  getInboundDomain: () => apiClient.get('/inbound-email/domain/'),
  verifyInboundDomain: () => apiClient.post('/inbound-email/domain/verify/'),
  deleteInboundDomain: () => apiClient.delete('/inbound-email/domain/'),
}

// Users API
export const usersAPI = {
  list: () => apiClient.get('/users/'),
  update: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/users/${id}/`, data),
  deactivate: (id: string) => apiClient.post(`/users/${id}/deactivate/`),
  invite: (data: Record<string, unknown>) => apiClient.post('/users/invitations/', data),
  listInvitations: () => apiClient.get('/users/invitations/'),
  resendInvitation: (id: string) => apiClient.post(`/users/invitations/${id}/resend/`),
}

// Session expiry tracking — updated by response interceptor
let _sessionExpiresAt: string | null = null

export function getSessionExpiresAt(): string | null {
  return _sessionExpiresAt
}

export function setSessionExpiresAt(value: string | null) {
  _sessionExpiresAt = value
}

// Auth API
export const authAPI = {
  passwordResetRequest: (email: string) =>
    apiClient.post('/auth/password-reset/', { email }),
  passwordResetConfirm: (uid: string, token: string, password: string) =>
    apiClient.post('/auth/password-reset/confirm/', { uid, token, password }),
  changePassword: (current_password: string, new_password: string, confirm_password: string) =>
    apiClient.post('/auth/change-password/', { current_password, new_password, confirm_password }),
  heartbeat: () => apiClient.post('/auth/heartbeat/'),
  mfaEnroll: () =>
    apiClient.post<{ secret: string; otpauth_uri: string }>('/auth/mfa/enroll/', {}),
  mfaEnrollConfirm: (code: string) =>
    apiClient.post('/auth/mfa/enroll/confirm/', { code }),
  mfaDisable: (code: string) => apiClient.post('/auth/mfa/disable/', { code }),
}

// Collections API
export const collectionsAPI = {
  getARInvoices: (params?: {
    search?: string
    technician?: string
    min_balance_cents?: number
    max_balance_cents?: number
    aging_buckets?: string
    min_days_past_due?: number
    max_days_past_due?: number
    status?: string
    business_unit?: string
    invoice_number?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    page?: number
    page_size?: number
  }) => apiClient.get('/collections/ar-invoices/', { params }),
  getARInvoiceLiveBalances: (ids: string[]) =>
    apiClient.get('/collections/ar-invoices/live-balances/', { params: { ids: ids.join(',') } }),
  getARInvoicesByCustomer: (params?: {
    search?: string
    technician?: string
    min_balance_cents?: number
    max_balance_cents?: number
    aging_buckets?: string
    status?: string
    business_unit?: string
    invoice_number?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    page?: number
    page_size?: number
  }) => apiClient.get('/collections/ar-invoices/by-customer/', { params }),
  exportARInvoices: (params?: {
    min_balance_cents?: number
    max_balance_cents?: number
    aging_buckets?: string
    search?: string
    technician?: string
    status?: string
    business_unit?: string
    invoice_number?: string
    sort_by?: string
    sort_order?: string
  }) => apiClient.get('/collections/ar-invoices/export/', { params, responseType: 'blob' }),
  addInvoicesToFlow: (flowId: string, invoiceIds: string[]) =>
    apiClient.post(`/flows/${flowId}/add-invoices/`, { invoice_ids: invoiceIds }),
  getFlows: () => apiClient.get('/flows/'),

  // Payments (ledger)
  getCollectedStats: (params?: { start?: string; end?: string }) =>
    apiClient.get<{
      total_cents: number
      count: number
      invoice_count: number
      outreach_total_cents: number
      outreach_count: number
      outreach_invoice_count: number
      whole_feed_cents: number
      whole_feed_count: number
      pending_cents: number
      pending_count: number
      period_start: string
      period_end: string
    }>('/payments/collected-stats/', { params }),

  /** The evidence file: the collected list as CSV, same filters as getPayments. */
  exportCollectedPayments: (params: {
    population: 'all' | 'chased' | 'outreach' | 'pending'
    start: string
    end: string
    search?: string
  }) => apiClient.get('/payments/export/', { params: { collected: 'true', ...params }, responseType: 'blob' }),

  getPayments: (params?: {
    search?: string
    payment_type?: string
    status?: string
    has_case?: string
    collected?: string
    /** With collected=true: all | chased (default) | outreach | pending. */
    population?: 'all' | 'chased' | 'outreach' | 'pending'
    start?: string
    end?: string
    payment_date_from?: string
    payment_date_to?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    page?: number
    page_size?: number
  }) => apiClient.get('/payments/', { params }),

  // Pending Payments
  getPendingPayments: (params?: {
    search?: string
    payment_type?: string
    status?: string
    posted_date_from?: string
    posted_date_to?: string
    technician_name?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    page?: number
    page_size?: number
  }) => apiClient.get('/pending-payments/', { params }),
  getPendingPaymentsSummary: () => apiClient.get('/pending-payments/summary/'),
  resolvePayment: (id: string, data: { resolution: 'deposited' | 'cancelled'; notes?: string | null }) =>
    apiClient.post(`/pending-payments/${id}/resolve/`, data),
}

// Unified API wrapper for React Query
export const api = {
  // Admin Dashboard
  getAdminMetrics: async (days?: number) => {
    const { data } = await apiClient.get('/dashboard/admin/', {
      params: days !== undefined ? { days } : undefined,
    })
    return data
  },

  // Memberships
  getLapsedMemberships: async () => {
    const { data } = await apiClient.get('/memberships/', { params: { lapsed: 'true' } })
    return data?.results || data || []
  },

  // Users
  getUsers: async () => {
    const { data } = await usersAPI.list()
    return data?.results || data || []
  },

  // Integrations
  getServiceTitanStatus: async () => {
    const { data } = await apiClient.get('/integrations/servicetitan/status/')
    return data
  },
  connectServiceTitan: async (credentials: { client_id: string; client_secret: string; app_id: string; tenant_id_st?: string; environment?: string }) => {
    const { data } = await apiClient.post('/integrations/servicetitan/connect/', credentials)
    return data
  },
  disconnectServiceTitan: async () => {
    await apiClient.delete('/integrations/servicetitan/disconnect/')
  },
  syncServiceTitan: async () => {
    const { data } = await apiClient.post('/integrations/servicetitan/sync/')
    return data
  },
  getServiceTitanFilter: async () => {
    const { data } = await apiClient.get('/integrations/servicetitan/filter/')
    return data as SyncFilter
  },
  updateServiceTitanFilter: async (data: SyncFilterUpdate) => {
    const { data: resp } = await apiClient.put('/integrations/servicetitan/filter/', data)
    return resp as SyncFilter
  },
  validateServiceTitanScopes: async () => {
    const { data } = await apiClient.post('/integrations/servicetitan/validate-scopes/')
    return data
  },
  getReportConfig: async () => {
    const { data } = await apiClient.get('/integrations/servicetitan/report-config/')
    return data
  },
  updateReportConfig: async (config: Record<string, unknown>) => {
    const { data } = await apiClient.put('/integrations/servicetitan/report-config/', { config })
    return data
  },
  getSTReportCategories: async () => {
    const { data } = await apiClient.get('/integrations/servicetitan/st-reports/categories/')
    return data
  },
  getSTReports: async (categoryId: string) => {
    const { data } = await apiClient.get(`/integrations/servicetitan/st-reports/categories/${categoryId}/reports/`)
    return data
  },
  getSTReportSchema: async (categoryId: string, reportId: string) => {
    const { data } = await apiClient.get(`/integrations/servicetitan/st-reports/categories/${categoryId}/reports/${reportId}/schema/`)
    return data
  },
  // ST Web Session
  getSTWebSession: async () => {
    const { data } = await apiClient.get('/integrations/st-web-session/')
    return data
  },
  updateSTWebSession: async (payload: { origin_url?: string; st_web_username?: string; st_web_password?: string; step2_enabled?: boolean }) => {
    const { data } = await apiClient.put('/integrations/st-web-session/', payload)
    return data
  },
  rotateSTWebSession: async () => {
    const { data } = await apiClient.post('/integrations/st-web-session/rotate/')
    return data
  },
  // Reports
  getRecoverySummary: async (params?: { start_date?: string; end_date?: string; period?: string }) => {
    const { data } = await apiClient.get('/reports/recovery-summary/', { params })
    return data
  },
  getAgentPerformance: async (params?: { start_date?: string; end_date?: string; period?: string }) => {
    const { data } = await apiClient.get('/reports/agent-performance/', { params })
    return data
  },

  // Combined integrations list for dashboard
  getIntegrations: async () => {
    const [stStatus, lkStatus] = await Promise.all([
      apiClient.get('/integrations/servicetitan/status/'),
      apiClient.get('/integrations/livekit/status/'),
    ])
    return [
      { type: 'servicetitan', ...stStatus.data },
      { type: 'livekit', ...lkStatus.data },
    ]
  },

}

// Tenants API (super admin)
export const tenantsAPI = {
  list: () => apiClient.get('/tenants/').then(res => res.data),
  // Tenants the current user may switch between (their own memberships).
  myTenants: () => apiClient.get('/auth/my-tenants/').then(res => res.data),
  // Membership management. Super admin: any tenant. Tenant admin: own tenant only.
  listMembers: (tenantId: string) =>
    apiClient.get(`/tenants/${tenantId}/members/`).then(res => res.data),
  assignMember: (tenantId: string, payload: { email: string; role_code: string }) =>
    apiClient.post(`/tenants/${tenantId}/members/`, payload).then(res => res.data),
  removeMember: (tenantId: string, userId: string) =>
    apiClient.delete(`/tenants/${tenantId}/members/${userId}/`).then(res => res.data),
}


export const voiceAgentsAPI = {
  list: (params?: Record<string, unknown>) => apiClient.get('/voice-agents/', { params }),
  get: (id: string) => apiClient.get(`/voice-agents/${id}/`),
  create: (data: Record<string, unknown>) => apiClient.post('/voice-agents/', data),
  update: (id: string, data: Record<string, unknown>) =>
    apiClient.patch(`/voice-agents/${id}/`, data),
  getPromptPreview: (id: string) => apiClient.get(`/voice-agents/${id}/prompt-preview/`),

  // Prompts

  // Default prompts per flow type
  listDefaultPrompts: () => apiClient.get('/voice-agents/default-prompts/'),

  // The personas this agent has an active prompt for. The Simulation tab
  // picks one, because a run must grade the prompt a real call assembles.
  listActivePrompts: (id: string) =>
    apiClient.get('/voice-agent-prompts/', {
      params: { voice_agent_id: id, is_active: true },
    }),

  // Simulations: play the scenario set against a draft prompt
  startSimulation: (
    id: string,
    data: { prompt_text: string; persona_name?: string; audio_pipeline?: string },
  ) =>
    apiClient.post(`/voice-agents/${id}/simulations/`, data),
  listSimulations: (id: string) =>
    apiClient.get('/voice-agent-simulations/', { params: { voice_agent: id } }),
  getSimulation: (runId: string) => apiClient.get(`/voice-agent-simulations/${runId}/`),
}
